import { useState } from "react";
import {
  Modal,
  Button,
  ButtonVariant,
  IconButton,
  IconButtonVariant,
  Radio
} from "shared/components";
import { roles } from "shared/constants/roles";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ApplicantType } from "shared/types";
import { cx } from "shared/utils";
import { getApplicantDisplayName } from "shared/utils/get-applicant-display-name";

import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { ApplicantOrInvitee } from "views/applications-id/components/ApplicantsTable/types";

export const ChangeRoleModal = ({
  availableRoles,
  onClose,
  onChange,
  applicant,
  isOpen,
  applicantsList
}: {
  availableRoles: ApplicantType[];
  onClose: () => void;
  onChange: (role: ApplicantType) => void;
  applicant?: ApplicantOrInvitee;
  isOpen: boolean;
  applicantsList: ApplicantOrInvitee[];
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const [selectedRole, setSelectedRole] = useState(
    applicant?.role as ApplicantType
  );

  const rolesList = roles.filter((role) =>
    availableRoles.includes(role.value as ApplicantType)
  );

  const numberOfApplicantsOrOccupants = applicantsList.filter((v) =>
    [ApplicantType.Applicant, ApplicantType.Occupant].includes(
      v.role as string as ApplicantType
    )
  ).length;

  const displayName = applicant
    ? getApplicantDisplayName(applicant)
    : "Unknown";

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      panelClassName="max-w-[670px] w-full"
    >
      <div className="p-6 border-b border-alpha-100">
        <div className="flex items-center justify-between">
          <h1 className="text-xl text-content-1000 font-bold">
            Change role for {displayName}
          </h1>
          <IconButton
            onClick={onClose}
            className="ml-auto"
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.TEXT_BASE}
          />
        </div>
        <p className="text-sm text-content-500">
          Application will use the screening settings for the selected role.
          Application will keep the original set of requested information, but
          any missing reports will be added.
        </p>
      </div>
      <div className="px-6 py-4 bg-background-900">
        <div
          className="w-full grid gap-4"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))"
          }}
        >
          {rolesList.length ? (
            rolesList.map((role) => {
              return (
                <div
                  key={role.value}
                  className={cx(
                    "bg-background-1000 gap-4 w-full rounded-lg p-6 hover:border-alpha-200 hover:shadow-sm transition-all duration-75 cursor-pointer",
                    selectedRole === role.value
                      ? "border border-primary-100 border-opacity-0"
                      : "border border-alpha-100 border-opacity-0",
                    numberOfApplicantsOrOccupants === 1 &&
                      role.value === "co_signer"
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  )}
                  onClick={() => {
                    if (
                      numberOfApplicantsOrOccupants === 1 &&
                      role.value === "co_signer"
                    )
                      return;
                    setSelectedRole(role.value);
                  }}
                >
                  <Radio
                    id="role-user"
                    name="role"
                    checked={selectedRole === role.value}
                    // Handled by the parent component
                    className="pointer-events-none"
                  />
                  <p className="text-sm font-bold pt-5">{role.label}</p>
                  <p className="text-sm font-normal text-content-400 pt-2">
                    {role.description(dictionary.property)}
                  </p>
                </div>
              );
            })
          ) : (
            <div>
              <p className="text-base font-bold py-5">
                {/* Should not be the case */}
                No available roles in this template
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-end px-6 py-4 border-t border-alpha-100 gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={!rolesList.length}
          variant={ButtonVariant.PRIMARY}
          onClick={() => onChange(selectedRole as ApplicantType)}
        >
          Change
        </Button>
      </div>
    </Modal>
  );
};
